//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  IGetDailyMetricsResponse,
  IGetDailyMetricsResult,
  IGetDailyMetricsRequest,
  IGetEngagementStatsResponse,
  IGetEngagementStatsRequest,
  IGetEngagementVideosResponse,
  IGetEngagementVideosResult,
  IGetEngagementVideosRequest,
  IGetSummaryMetricsResponse,
  IGetSummaryMetricsRequest,
  ITikTokAudienceDailyMetrics,
  ITikTokEngagementStats,
  IGetAudienceOnlineFollowersHeatmapRequest,
  IGetAudienceOnlineFollowersHeatmapResponse,
  ITikTokOnlineFollowersHeatmap,
  IGetAudienceBestTimesToPostRequest,
  IGetAudienceBestTimesToPostResponse,
  ITikTokBestTimesToPost,
} from '@apis/tiktok-analytics/tiktokAnalyticsApiModel';
import { deserializeVideosWithMetrics } from '@apis/tiktok-analytics/tiktokAnalyticsApiUtils';
import { isDefined } from '@utils/filterPredicates';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const TIKTOK_DEFAULT_ENDPOINT = '/api/v1';

const tiktokGatewayAnalyticsFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'tiktok_gateway',
    `${TIKTOK_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 * Gets the client's TikTok audience daily stats
 *
 * GET /api/v1/user/daily-metrics
 */
export const getAudienceDailyStats = async (
  req: IGetDailyMetricsRequest,
): Promise<IGetDailyMetricsResult> => {
  const params = makeParams(req);
  const res = await tiktokGatewayAnalyticsFetch(
    `/user/daily-metrics?${params}`,
  );

  return (
    await res.json<IGetDailyMetricsResponse>((res) => ({
      data: {
        ...res.data,
        earliestDate: isDefined(res.data.earliestDate)
          ? new Date(res.data.earliestDate)
          : undefined,
      },
    }))
  ).data;
};

/**
 * Gets the client's TikTok audience summary stats
 *
 * GET /api/v1/user/summary-metrics
 */
export const getAudienceSummaryStats = async (
  req: IGetSummaryMetricsRequest,
): Promise<ITikTokAudienceDailyMetrics> => {
  const params = makeParams(req);
  const res = await tiktokGatewayAnalyticsFetch(
    `/user/summary-metrics?${params}`,
  );

  return (await res.json<IGetSummaryMetricsResponse>()).data;
};

/**
 * Gets the client's most recent TikTok online followers heatmap
 *
 * GET /api/v1/user/online-followers/averaged-week
 */
export const getAudienceOnlineFollowersHeatmap = async (
  req: IGetAudienceOnlineFollowersHeatmapRequest,
): Promise<ITikTokOnlineFollowersHeatmap> => {
  const params = makeParams(req);
  const res = await tiktokGatewayAnalyticsFetch(
    `/user/online-followers/averaged-week?${params}`,
  );

  return (await res.json<IGetAudienceOnlineFollowersHeatmapResponse>()).data;
};

/**
 * Gets the client's most recent top three times to post for each weekday
 *
 * GET /api/v1/user/online-followers/best-times-to-post
 */
export const getAudienceBestTimesToPost = async (
  req: IGetAudienceBestTimesToPostRequest,
): Promise<ITikTokBestTimesToPost> => {
  const params = makeParams(req);
  const res = await tiktokGatewayAnalyticsFetch(
    `/user/online-followers/best-times-to-post?${params}`,
  );

  return (await res.json<IGetAudienceBestTimesToPostResponse>()).data;
};

/**
 * Gets the client's TikTok engagement stats
 *
 * GET /api/v1/engagement
 */
export const getEngagementStats = async (
  req: IGetEngagementStatsRequest,
): Promise<ITikTokEngagementStats> => {
  const params = makeParams(req);
  const res = await tiktokGatewayAnalyticsFetch(`/engagement?${params}`);

  return (await res.json<IGetEngagementStatsResponse>()).data;
};

/**
 * Gets the client's TikTok videos
 *
 * GET /api/v1/engagement/videos
 */
export const getEngagementVideos = async (
  req: IGetEngagementVideosRequest,
): Promise<IGetEngagementVideosResult> => {
  const params = makeParams(req);
  const res = await tiktokGatewayAnalyticsFetch(`/engagement/videos?${params}`);

  return (
    await res.json<IGetEngagementVideosResponse>((res) => ({
      data: {
        ...res.data,
        videos: deserializeVideosWithMetrics(res.data.videos),
      },
    }))
  ).data;
};

/**
 * Gets the client's TikTok videos CSV
 *
 * GET /api/v1/engagement/videos/csv
 */
export const getEngagementVideosCSV = async (
  req: IGetEngagementVideosRequest,
): Promise<string> => {
  const params = makeParams(req);
  const res = await tiktokGatewayAnalyticsFetch(
    `/engagement/videos/csv?${params}`,
  );

  return res.text();
};
